// src/components/pages/.../SuccessInquiry.jsx
import React, { useEffect, useMemo, useState } from "react";
import { Box, Paper, Stack, Typography, Button } from "@mui/material";
import { useNavigate } from "react-router-dom";
import VerifiedIcon from "@mui/icons-material/Verified";
import {
  getLangFromCookie,
  LANG_COOKIE,
} from "../../../../../Utils/LangCookie";

/* ---------------- i18n ---------------- */

const I18N = {
  title: {
    ZH: "【送出完成】",
    EN: "[Submission Completed]",
    JP: "【送信完了】",
  },
  line1: {
    ZH: "感謝您的詢價。",
    EN: "Thank you for your inquiry.",
    JP: "お問い合わせありがとうございます。",
  },
  line2: {
    ZH: "我們將確認內容後，由專員依序與您聯繫。",
    EN: "After reviewing the details, our representative will contact you in order.",
    JP: "内容を確認の上、担当者より順次ご連絡させていただきます。",
  },
  line3: {
    ZH: "敬請稍候片刻，等待我們的回覆，謝謝。",
    EN: "We kindly ask for your patience while you wait for our reply.",
    JP: "恐れ入りますが、返信まで今しばらくお待ちいただきますようお願い申し上げます。",
  },
  actions: {
    history: {
      ZH: "查看查詢紀錄",
      EN: "See Inquiries",
      JP: "見積り履歴を確認する",
    },
    browseProducts: {
      ZH: "返回商品資訊頁面",
      EN: "Browse Product",
      JP: "商品情報ページに戻る",
    },
  },
};

/* ---------------- confetti helpers ---------------- */

function rand(min, max) {
  return Math.random() * (max - min) + min;
}
function pick(arr) {
  return arr[Math.floor(Math.random() * arr.length)];
}

export default function SuccessInquiry() {
  const nav = useNavigate();
  const [lang, setLang] = useState(() => getLangFromCookie() || "ZH");

  // global (page) confetti + inner (paper) confetti
  const [poppersBg, setPoppersBg] = useState([]);
  const [poppersCard, setPoppersCard] = useState([]);

  useEffect(() => {
    const syncLang = () => {
      const c =
        getLangFromCookie() || localStorage.getItem(LANG_COOKIE) || "ZH";
      if (c === "EN" || c === "JP" || c === "ZH") setLang(c);
      else setLang("ZH");
    };
    syncLang();
    window.addEventListener("app:languageChanged", syncLang);
    return () => window.removeEventListener("app:languageChanged", syncLang);
  }, []);

  const font = {
    ZH: "Line Seed TW",
    EN: "Line Seed Sans",
    JP: "Line Seed JP",
  };

  // ✅ generate party pop once when page loads (two layers)
  useEffect(() => {
    const shapes = ["circle", "rect"];
    const colors = [
      "#22c55e",
      "#60a5fa",
      "#f59e0b",
      "#ef4444",
      "#a78bfa",
      "#14b8a6",
      "#f472b6",
      "#94a3b8",
    ];

    const now = Date.now();

    // --- Layer 1: page background (behind paper) ---
    const BG_COUNT = 90;
    const bgItems = Array.from({ length: BG_COUNT }).map((_, i) => {
      const fromLeft = i % 2 === 0;
      const startX = fromLeft ? rand(6, 18) : rand(82, 94);
      const startY = rand(10, 22);
      const dx = fromLeft ? rand(20, 60) : rand(-60, -20);
      const dy = rand(20, 65);
      const size = rand(6, 12);
      const rotate = rand(0, 360);
      const duration = rand(900, 1400);
      const delay = rand(0, 90);

      return {
        id: `${now}-bg-${i}`,
        startX,
        startY,
        dx,
        dy,
        size,
        rotate,
        duration,
        delay,
        color: pick(colors),
        shape: pick(shapes),
      };
    });

    // --- Layer 2: inside paper (visible behind text) ---
    // Start near top-middle of the card, explode gently.
    const CARD_COUNT = 55;
    const cardItems = Array.from({ length: CARD_COUNT }).map((_, i) => {
      const startX = rand(35, 65); // near center
      const startY = rand(8, 18); // near top inside card
      const dx = rand(-18, 18); // smaller spread
      const dy = rand(18, 45);
      const size = rand(6, 11);
      const rotate = rand(0, 360);
      const duration = rand(850, 1250);
      const delay = rand(0, 90);

      return {
        id: `${now}-card-${i}`,
        startX,
        startY,
        dx,
        dy,
        size,
        rotate,
        duration,
        delay,
        color: pick(colors),
        shape: pick(shapes),
      };
    });

    setPoppersBg(bgItems);
    setPoppersCard(cardItems);

    const t = setTimeout(() => {
      setPoppersBg([]);
      setPoppersCard([]);
    }, 1900);

    return () => clearTimeout(t);
  }, []);

  // ✅ Force page to start at top so the centered card is visible
  useEffect(() => {
    // next frame ensures layout is ready before scrolling
    requestAnimationFrame(() => {
      window.scrollTo({ top: 0, left: 0, behavior: "auto" });
    });
  }, []);

  const confettiKeyframes = useMemo(
    () => ({
      "@keyframes confetti-fly": {
        "0%": { transform: "translate(0,0) rotate(0deg)", opacity: 0 },
        "8%": { opacity: 1 },
        "100%": {
          opacity: 0,
          transform: "translate(var(--dx), var(--dy)) rotate(var(--rot))",
        },
      },
    }),
    []
  );

  return (
    <Box
      sx={{
        width: "100%",
        minHeight: "100vh", // was 60vh
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        px: 2,
        py: 6,
        boxSizing: "border-box",
        position: "relative",
        overflow: "hidden",
        ...confettiKeyframes,
      }}
    >
      {/* ✅ Background confetti (page background) */}
      <Box
        aria-hidden
        sx={{
          position: "absolute",
          inset: 0,
          zIndex: 0,
          pointerEvents: "none",
        }}
      >
        {poppersBg.map((p) => (
          <Box
            key={p.id}
            sx={{
              position: "absolute",
              left: `${p.startX}%`,
              top: `${p.startY}%`,
              width: p.shape === "circle" ? p.size : p.size * 1.2,
              height: p.size,
              borderRadius: p.shape === "circle" ? "999px" : "3px",
              background: p.color,
              opacity: 0,
              "--dx": `${p.dx}vw`,
              "--dy": `${p.dy}vh`,
              "--rot": `${p.rotate}deg`,
              animation: `confetti-fly ${p.duration}ms ease-out ${p.delay}ms forwards`,
              filter: "drop-shadow(0 2px 6px rgba(0,0,0,0.08))",
            }}
          />
        ))}
      </Box>

      <Paper
        variant="outlined"
        sx={{
          width: "100%",
          maxWidth: 720,
          borderRadius: 2,
          p: { xs: 3, md: 5 },
          position: "relative",
          zIndex: 1,
          overflow: "hidden", // ✅ confetti clipped inside card
          // ✅ keep it white, but allow confetti to show through
          bgcolor: "rgba(255,255,255,0.90)", // was 0.96 (too opaque)
          backdropFilter: "blur(2px)",
        }}
      >
        {/* ✅ Inner confetti (inside the white box, behind text) */}
        <Box
          aria-hidden
          sx={{
            position: "absolute",
            inset: 0,
            zIndex: 0,
            pointerEvents: "none",
          }}
        >
          {poppersCard.map((p) => (
            <Box
              key={p.id}
              sx={{
                position: "absolute",
                left: `${p.startX}%`,
                top: `${p.startY}%`,
                width: p.shape === "circle" ? p.size : p.size * 1.2,
                height: p.size,
                borderRadius: p.shape === "circle" ? "999px" : "3px",
                background: p.color,
                opacity: 0,

                "--dx": `${p.dx}vw`,
                "--dy": `${p.dy}vh`,
                "--rot": `${p.rotate}deg`,
                animation: `confetti-fly ${p.duration}ms ease-out ${p.delay}ms forwards`,

                // ✅ make it “inside background” not too strong
                filter: "blur(0.15px) drop-shadow(0 2px 5px rgba(0,0,0,0.06))",
                mixBlendMode: "multiply", // ✅ looks like it's printed on paper
              }}
            />
          ))}

          {/* ✅ optional soft wash to keep text readable */}
          <Box
            sx={{
              position: "absolute",
              inset: 0,
              background:
                "radial-gradient(circle at 50% 35%, rgba(255,255,255,0.0) 0%, rgba(255,255,255,0.55) 55%, rgba(255,255,255,0.78) 100%)",
            }}
          />
        </Box>

        {/* ✅ Content above inner confetti */}
        <Stack
          spacing={2.2}
          alignItems="center"
          sx={{ position: "relative", zIndex: 1 }}
        >
          <Typography
            sx={{
              fontFamily: font[lang],
              fontWeight: 700,
              fontSize: { xs: 18, md: 30 },
              textAlign: "center",
            }}
          >
            {I18N.title[lang]}
          </Typography>

          <VerifiedIcon
            sx={{
              color: "#22c55e",
              fontSize: { xs: 42, md: 62 },
            }}
          />

          <Stack spacing={1.2} sx={{ width: "100%" }}>
            <Typography
              sx={{
                fontFamily: font[lang],
                textAlign: "center",
                fontSize: { xs: 12, md: 20 },
                fontWeight: 400,
              }}
            >
              {I18N.line1[lang]}
            </Typography>
            <Typography
              sx={{
                fontFamily: font[lang],
                textAlign: "center",
                fontSize: { xs: 12, md: 20 },
                fontWeight: 400,
              }}
            >
              {I18N.line2[lang]}
            </Typography>
            <Typography
              sx={{
                fontFamily: font[lang],
                textAlign: "center",
                fontSize: { xs: 12, md: 20 },
                fontWeight: 400,
              }}
            >
              {I18N.line3[lang]}
            </Typography>
          </Stack>

          <Stack
            direction={{ xs: "column", sm: "row" }}
            spacing={1.25}
            sx={{ pt: 2, width: "100%", justifyContent: "center" }}
          >
            <Button
              variant="contained"
              onClick={() => nav("/商品資訊")}
              sx={{ fontFamily: font[lang], minWidth: { xs: "100%", sm: 220 } }}
            >
              {I18N.actions.browseProducts[lang]}
            </Button>

            <Button
              variant="outlined"
              onClick={() => nav("/詢價/歷史")}
              sx={{ fontFamily: font[lang], minWidth: { xs: "100%", sm: 220 } }}
            >
              {I18N.actions.history[lang]}
            </Button>
          </Stack>
        </Stack>
      </Paper>
    </Box>
  );
}
